<template>
  <div
    class="auth-form-container w-full max-w-[400px] mx-auto"
    :aria-label="STRINGS.aria.labels.loginForm"
  >
    <h2
      class="auth-heading font-display text-2xl font-extrabold text-arena-text-bright tracking-[0.06em] text-center mb-6"
    >
      {{ STRINGS.auth.buttons.logIn }}
    </h2>

    <form
      class="auth-card flex flex-col gap-4 p-6 rounded-xl border border-arena-border bg-arena-surface/80 backdrop-blur-sm"
      @submit.prevent="handleSubmit"
      autocomplete="on"
      novalidate
    >
      <!-- Email -->
      <div class="flex flex-col gap-1.5">
        <label for="login-email" class="text-sm font-semibold text-arena-text-bright">
          {{ STRINGS.auth.labels.email }}
        </label>
        <div class="relative">
          <InputText
            id="login-email"
            ref="emailInput"
            v-model="email"
            type="email"
            name="email"
            class="auth-input min-h-12 touch-manipulation w-full"
            :placeholder="STRINGS.auth.placeholders.email"
            autocomplete="username"
            :aria-invalid="!!localError"
            data-testid="login-email-input"
            @keyup.enter="handleSubmit"
          />
        </div>
      </div>

      <!-- Password -->
      <div class="flex flex-col gap-1.5">
        <label for="login-password" class="text-sm font-semibold text-arena-text-bright">
          {{ STRINGS.auth.labels.password }}
        </label>
        <div class="relative">
          <InputText
            id="login-password"
            v-model="password"
            name="password"
            :type="showPassword ? 'text' : 'password'"
            class="auth-input min-h-12 touch-manipulation w-full pr-10"
            :placeholder="STRINGS.auth.placeholders.password"
            autocomplete="current-password"
            data-testid="login-password-input"
            @keyup.enter="handleSubmit"
          />
          <button
            type="button"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-arena-text-muted hover:text-arena-text-bright transition-colors p-1"
            :aria-label="STRINGS.aria.labels.togglePasswordVisibility"
            :aria-pressed="showPassword"
            @click="showPassword = !showPassword"
          >
            <i :class="showPassword ? 'pi pi-eye-slash' : 'pi pi-eye'" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- Error display -->
      <p
        v-if="displayError"
        ref="errorRef"
        class="text-sm text-error-500 text-center m-0"
        role="alert"
        aria-live="polite"
        tabindex="-1"
      >
        {{ displayError }}
      </p>

      <!-- Submit button -->
      <PrimeButton
        :disabled="!canSubmit"
        :aria-label="STRINGS.aria.labels.logIn"
        aria-busy="false"
        data-testid="login-submit-button"
        class="min-h-12 touch-manipulation mt-2"
        :full-width="true"
        @click="handleSubmit"
      >
        <template v-if="auth.isLoading.value">
          {{ STRINGS.auth.buttons.loggingIn }}<LoadingDots />
        </template>
        <template v-else>
          {{ STRINGS.auth.buttons.logIn }}
        </template>
      </PrimeButton>
    </form>

    <!-- Footer links -->
    <div class="auth-footer flex flex-col items-center gap-2 mt-6 text-sm text-arena-text-muted">
      <p class="m-0">
        {{ STRINGS.auth.links.noAccount }}
        <button
          class="text-energy-primary hover:text-primary-500 underline underline-offset-2 bg-transparent border-none cursor-pointer font-semibold p-0"
          @click="emit('navigate', 'signup')"
          data-testid="login-to-signup-link"
        >
          {{ STRINGS.auth.links.createAccount }}
        </button>
      </p>
      <button
        class="text-energy-primary hover:text-primary-500 underline underline-offset-2 bg-transparent border-none cursor-pointer text-sm p-0"
        @click="emit('navigate', 'forgot-password')"
        data-testid="login-forgot-password-link"
      >
        {{ STRINGS.auth.links.forgotPassword }}
      </button>
      <button
        class="text-arena-text-muted hover:text-arena-text-bright bg-transparent border-none cursor-pointer text-sm p-0"
        @click="emit('navigate', 'homepage')"
        data-testid="login-back-link"
      >
        {{ STRINGS.auth.links.backToHome }}
      </button>
      <template v-if="isMockAuth">
        <hr class="border-arena-border w-full my-1" />
        <button
          class="text-energy-primary hover:text-primary-500 bg-transparent border-none cursor-pointer text-sm p-0 font-semibold"
          data-testid="dev-login-button"
          @click="handleDevLogin"
        >
          Dev: Skip Login
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, nextTick } from 'vue';
import InputText from 'primevue/inputtext';
import PrimeButton from './ui/PrimeButton.vue';
import LoadingDots from './ui/LoadingDots.vue';
import { useAuth } from '@/composables/useAuth';
import { isAuthError } from '@/types/auth';
import { AUTH_CONFIG } from '@/config/constants';
import STRINGS from '@/constants/strings';
import { reportClientError, ErrorLocation } from '@/telemetry/errors';

const emit = defineEmits<{
  success: [];
  navigate: [target: 'signup' | 'forgot-password' | 'homepage'];
}>();

const auth = useAuth();
const isMockAuth = AUTH_CONFIG.isMockAuthEnabled();

const email = ref('');
const password = ref('');
const showPassword = ref(false);
const localError = ref('');
const emailInput = ref();
const errorRef = ref<HTMLElement>();

const displayError = computed(() => {
  if (auth.rateLimitCountdown.value > 0) {
    return STRINGS.templates.rateLimitCountdown(auth.rateLimitCountdown.value);
  }
  if (auth.error.value) {
    return auth.error.value.message;
  }
  return localError.value;
});

const canSubmit = computed(() => {
  return (
    email.value.trim().length > 0 &&
    password.value.length > 0 &&
    !auth.isLoading.value &&
    auth.rateLimitCountdown.value === 0
  );
});

async function handleSubmit(): Promise<void> {
  if (!canSubmit.value) return;

  localError.value = '';
  auth.clearError();

  if (!email.value.trim()) {
    localError.value = STRINGS.auth.errors.emailRequired;
    focusError();
    return;
  }

  try {
    await auth.login({ email: email.value.trim(), password: password.value });
    emit('success');
  } catch (err: unknown) {
    if (!isAuthError(err)) {
      reportClientError(err, ErrorLocation.AuthLoginUnexpected);
    }
    focusError();
  }
}

async function handleDevLogin(): Promise<void> {
  if (auth.devLogin) {
    await auth.devLogin();
    emit('success');
  }
}

function focusError(): void {
  nextTick(() => {
    errorRef.value?.focus();
  });
}

onMounted(() => {
  nextTick(() => {
    const el = emailInput.value?.$el ?? emailInput.value;
    const input = el?.querySelector?.('input') ?? el;
    if (input && typeof input.focus === 'function') {
      input.focus();
    }
  });
});
</script>

<style scoped>
.auth-heading {
  animation: homepageEntrance 0.8s ease-out both;
}

.auth-card {
  animation: homepageEntrance 0.8s ease-out 0.2s both;
}

.auth-footer {
  animation: homepageEntrance 0.8s ease-out 0.3s both;
}

@keyframes homepageEntrance {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.auth-input {
  padding: 0.75rem 1rem;
  border: 2px solid var(--color-arena-border);
  border-radius: var(--radius-input, 0.5rem);
  font-size: 1rem;
  transition: none;
  outline: none;
  background: rgba(26, 22, 18, 0.8);
  color: var(--color-arena-text-bright);
}

.auth-input:focus {
  outline: none;
}

.auth-input::placeholder {
  color: var(--color-arena-text-muted);
}

@media (prefers-reduced-motion: reduce) {
  .auth-heading,
  .auth-card,
  .auth-footer {
    animation: none !important;
  }
}
</style>
